package models

import utils.CategoryUtils

/**
 * Lightweight representation of a custom category for budget category building.
 * Platform API services map their own CustomCategoryData to this.
 */
data class BudgetCustomCategory(
    val primaryName: String,
    val secondaryName: String,
    val isIncome: Boolean
)

/**
 * A subcategory within a budget group.
 * @param code Plaid detailed code (e.g., "FOOD_AND_DRINK_GROCERIES") or custom name
 * @param displayName Localized display name
 */
data class BudgetSubcategory(
    val code: String,
    val displayName: String
)

/**
 * A primary-level budget category group that can be collapsed/expanded.
 * @param code Plaid primary code (e.g., "FOOD_AND_DRINK") or custom primary name
 * @param displayName Localized display name
 * @param icon Emoji icon
 * @param isIncome Whether this group is an income category
 * @param subcategories Detailed subcategories within this group
 */
data class BudgetCategoryGroup(
    val code: String,
    val displayName: String,
    val icon: String,
    val iconId: String = "",
    val isIncome: Boolean,
    val subcategories: List<BudgetSubcategory>
)

/**
 * Single source of truth for budget categories.
 * Derives the hierarchy from PlaidCategories + user custom categories.
 * Replaces all hardcoded budget category lists.
 */
object BudgetCategoryProvider {

    private val incomePrimaries = setOf(
        PlaidCategories.INCOME,
        PlaidCategories.TRANSFER_IN
    )

    private val expensePrimaries = listOf(
        PlaidCategories.BANK_FEES,
        PlaidCategories.ENTERTAINMENT,
        PlaidCategories.FAMILY,
        PlaidCategories.FOOD_AND_DRINK,
        PlaidCategories.GENERAL_MERCHANDISE,
        PlaidCategories.GENERAL_SERVICES,
        PlaidCategories.GOVERNMENT_AND_NON_PROFIT,
        PlaidCategories.HOME_IMPROVEMENT,
        PlaidCategories.LOAN_PAYMENTS,
        PlaidCategories.MEDICAL,
        PlaidCategories.PERSONAL_CARE,
        PlaidCategories.RENT_AND_UTILITIES,
        PlaidCategories.TRANSFER_OUT,
        PlaidCategories.TRANSPORTATION,
        PlaidCategories.TRAVEL
    )

    /**
     * Returns the full budget category hierarchy: income groups first, then expense groups.
     * Custom categories are merged into matching primary groups or added as new groups.
     */
    fun getCategories(customCategories: List<BudgetCustomCategory> = emptyList()): List<BudgetCategoryGroup> {
        // Build a map of primary → list of detailed PlaidCategory entries
        val plaidByPrimary = PlaidCategories.allCategories.groupBy { it.primary }

        // Group custom categories by primary name (case-insensitive match to Plaid display names)
        val customByPrimary = customCategories.groupBy { it.primaryName }

        val incomeGroups = buildGroups(
            primaries = incomePrimaries.toList(),
            plaidByPrimary = plaidByPrimary,
            customByPrimary = customByPrimary,
            isIncome = true
        )

        val expenseGroups = buildGroups(
            primaries = expensePrimaries,
            plaidByPrimary = plaidByPrimary,
            customByPrimary = customByPrimary,
            isIncome = false
        )

        // Add custom primary groups that don't match any Plaid primary
        val allPlaidDisplayNames = (incomePrimaries + expensePrimaries).map {
            CategoryUtils.formatCategoryName(it).lowercase()
        }.toSet()

        val customIncomePrimaries = customCategories
            .filter { it.isIncome && it.primaryName.lowercase() !in allPlaidDisplayNames }
            .groupBy { it.primaryName }
            .map { (primaryName, customs) ->
                BudgetCategoryGroup(
                    code = "CUSTOM:$primaryName",
                    displayName = primaryName,
                    icon = CategoryUtils.getCategoryIcon(primaryName),
                    iconId = CategoryUtils.getCategoryIconId(primaryName),
                    isIncome = true,
                    subcategories = customs.map { custom ->
                        BudgetSubcategory(
                            code = "CUSTOM:${custom.primaryName}/${custom.secondaryName}",
                            displayName = custom.secondaryName
                        )
                    }
                )
            }

        val customExpensePrimaries = customCategories
            .filter { !it.isIncome && it.primaryName.lowercase() !in allPlaidDisplayNames }
            .groupBy { it.primaryName }
            .map { (primaryName, customs) ->
                BudgetCategoryGroup(
                    code = "CUSTOM:$primaryName",
                    displayName = primaryName,
                    icon = CategoryUtils.getCategoryIcon(primaryName),
                    iconId = CategoryUtils.getCategoryIconId(primaryName),
                    isIncome = false,
                    subcategories = customs.map { custom ->
                        BudgetSubcategory(
                            code = "CUSTOM:${custom.primaryName}/${custom.secondaryName}",
                            displayName = custom.secondaryName
                        )
                    }
                )
            }

        // Merge custom groups into the sorted lists by display name
        val allIncome = (incomeGroups + customIncomePrimaries).sortedBy { it.displayName.lowercase() }
        val allExpense = (expenseGroups + customExpensePrimaries).sortedBy { it.displayName.lowercase() }
        return allIncome + allExpense
    }

    private fun buildGroups(
        primaries: List<String>,
        plaidByPrimary: Map<String, List<PlaidCategory>>,
        customByPrimary: Map<String, List<BudgetCustomCategory>>,
        isIncome: Boolean
    ): List<BudgetCategoryGroup> {
        return primaries.map { primaryCode ->
            val primaryDisplayName = CategoryUtils.formatCategoryNameLocalized(primaryCode)

            // Plaid subcategories
            val plaidSubs = (plaidByPrimary[primaryCode] ?: emptyList()).map { plaidCat ->
                val subCode = plaidCat.detailed.removePrefix("${primaryCode}_")
                BudgetSubcategory(
                    code = plaidCat.detailed,
                    displayName = CategoryUtils.formatCategoryNameLocalized(subCode)
                )
            }

            // Custom subcategories matching this primary (by display name, case-insensitive)
            val matchingCustoms = customByPrimary.entries
                .filter { (customPrimary, customs) ->
                    customPrimary.equals(primaryDisplayName, ignoreCase = true) ||
                    customPrimary.equals(CategoryUtils.formatCategoryName(primaryCode), ignoreCase = true)
                }
                .flatMap { it.value }
                .filter { it.isIncome == isIncome }
                .map { custom ->
                    BudgetSubcategory(
                        code = "CUSTOM:${custom.primaryName}/${custom.secondaryName}",
                        displayName = custom.secondaryName
                    )
                }

            BudgetCategoryGroup(
                code = primaryCode,
                displayName = primaryDisplayName,
                icon = CategoryUtils.getCategoryIcon(primaryCode),
                iconId = CategoryUtils.getCategoryIconId(primaryCode),
                isIncome = isIncome,
                subcategories = plaidSubs + matchingCustoms
            )
        }
    }
}
